import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGetIncidents } from '../api/queries';
import { getCompanies } from '../api/company';
import { useAuth } from '../hooks/useAuth';
import { Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { APP_ROUTES } from '../routes';
import { getStatusColor } from '../utils/incidentUtils';
import Loader from '../components/Loader';
import Error from '../components/Error';
import EmptyState from '../components/EmptyState';

export default function IncidentList() {
    const { state: authState } = useAuth();
    const currentUser = authState.user;
    const navigate = useNavigate();
    const { t } = useLanguage();

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(currentUser?.company_id);
    const itemsPerPage = 10;

    const isSuperAdmin = currentUser?.roles.includes('super-admin');
    const canCreateIncident = currentUser?.roles.some(r => ['admin', 'incidence-manager'].includes(r));

    const { data: companies } = useQuery({ 
        queryKey: ['companies'], 
        queryFn: getCompanies, 
        enabled: isSuperAdmin 
    });

    const { data, isLoading, error } = useGetIncidents(currentPage, itemsPerPage, 'created_at', 'desc', selectedCompanyId);

    const incidents = data?.incidents || [];
    const totalPages = data ? Math.ceil(data.total / itemsPerPage) : 1;

    const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedCompanyId(e.target.value || undefined);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handleRowClick = (id: string) => {
        navigate(APP_ROUTES.INCIDENT_DETAIL.replace(':id', id));
    };

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="mb-8">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('incidentList.title')}</h1>
                        <p className="text-gray-600">{t('incidentList.subtitle')}</p>
                    </div>
                    {canCreateIncident && (
                        <button onClick={() => navigate(APP_ROUTES.SUBMIT_INCIDENT)} className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700">Create Incident</button>
                    )}
                </div>

                {isSuperAdmin && (
                    <div className="mt-4">
                        <label htmlFor="company-select" className="block text-sm font-medium text-gray-700">Filter by Company</label>
                        <select id="company-select" onChange={handleCompanyChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                            <option value="">All Companies</option>
                            {companies?.map(company => (
                                <option key={company.id} value={company.id}>{company.name}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {isLoading && <Loader />}
            {error && <Error message={`${t('common.error')}: ${error.message}`} />}
            {!isLoading && !error && incidents.length === 0 && (
                <EmptyState 
                    title={isSuperAdmin && !selectedCompanyId ? "Please select a company" : t('incidentList.noIncidentsTitle')}
                    message={isSuperAdmin && !selectedCompanyId ? "Select a company from the dropdown to view its incidents." : t('incidentList.noIncidentsMsg')}
                />
            )}

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.status')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.priority')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.created')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.user')}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {incidents.map((incident) => (
                                <tr key={incident.id} onClick={() => handleRowClick(incident.id)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                                    <td className="px-6 py-4"><div className="text-sm font-medium text-gray-900">{incident.title}</div><div className="text-sm text-gray-500 truncate max-w-md">{incident.description}</div></td>
                                    <td className="px-6 py-4 whitespace-nowrap"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(incident.status)}`}>{t(`status.${incident.status}`).toUpperCase()}</span></td>
                                    <td className="px-6 py-4 whitespace-nowrap"><span className={`text-sm font-medium ${incident.priority === 'critical' ? 'text-red-600' : incident.priority === 'high' ? 'text-orange-600' : incident.priority === 'medium' ? 'text-blue-600' : 'text-gray-600'}`}>{t(`priority.${incident.priority}`).toUpperCase()}</span></td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"><div className="flex items-center"><Clock className="h-4 w-4 mr-1" />{new Date(incident.created_at).toLocaleDateString()}</div></td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{incident.user_id}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {totalPages > 1 && (
                <div className="mt-6 flex justify-between items-center">
                    <button onClick={handlePreviousPage} disabled={currentPage === 1} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg disabled:opacity-50">{t('incidentList.previous')}</button>
                    <span className="text-gray-600">{t('incidentList.page')} {currentPage} of {totalPages}</span>
                    <button onClick={handleNextPage} disabled={currentPage === totalPages} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg disabled:opacity-50">{t('incidentList.next')}</button>
                </div>
            )}
        </div>
    );
}
